import React, { useState } from 'react';

/**
 * Type-the-code fallback for magic links. The installed (standalone) app and
 * in-app email browsers open the emailed link in a different browser context,
 * so the email also carries a 6-digit code — typing it here makes THIS
 * context perform the verify, and the session lands where you're standing.
 * Renders inside any surface that just sent a link.
 */
const styles = `
  .ftcode { margin-top: 16px; padding-top: 14px; border-top: 1px solid rgba(0,0,0,.08); }
  .ftcode-l { font-size: 13px; color: #6E6E73; line-height: 1.5; margin: 0 0 10px; }
  .ftcode-l b { color: #1D1D1F; font-weight: 600; }
  .ftcode-row { display: flex; gap: 8px; }
  .ftcode-in {
    flex: 1; min-width: 0; max-width: 190px; background: #fff; border: 1px solid #EBEBED;
    border-radius: 12px; font-size: 20px; font-weight: 600; letter-spacing: 6px;
    font-family: 'SF Mono', ui-monospace, Menlo, monospace; padding: 11px 14px;
    color: #1D1D1F; outline: none; text-align: center;
  }
  .ftcode-in:focus { box-shadow: 0 0 0 3px rgba(0,0,0,.07); }
  .ftcode-btn {
    background: #000; color: #fff; border: none; cursor: pointer; border-radius: 980px;
    padding: 12px 22px; font: inherit; font-size: 14px; font-weight: 600; min-height: 44px;
  }
  .ftcode-btn:disabled { opacity: .4; cursor: default; }
  .ftcode-err { font-size: 12.5px; color: #FF3B30; margin-top: 8px; }
`;

export default function MagicCodeEntry({ email, redirectTo = '/home' }) {
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed) || checking) return;
    setChecking(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/verify-code', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: trimmed }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'That code did not work');
      if (body.token) localStorage.setItem('auth_token', body.token);
      window.location.assign(redirectTo);
    } catch (err) {
      setChecking(false);
      setError(err.message);
    }
  };

  return (
    <div className="ftcode">
      <style>{styles}</style>
      <p className="ftcode-l">
        Link opening in the wrong browser — like from the Home Screen app?{' '}
        <b>Type the code from the email instead.</b>
      </p>
      <form className="ftcode-row" onSubmit={submit}>
        <input
          className="ftcode-in"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          aria-label="Sign-in code"
          value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(null); }}
          disabled={checking}
        />
        <button type="submit" className="ftcode-btn" disabled={checking || code.trim().length !== 6}>
          {checking ? 'Checking…' : 'Sign in'}
        </button>
      </form>
      {error ? <div className="ftcode-err">{error}</div> : null}
    </div>
  );
}
